import { inject, Injectable } from '@angular/core';
import mixpanel from 'mixpanel-browser';
import { ConfigService } from './config.service';
import { VideoService } from './video.service';
import { CurrentUser } from '@core/models/current-user';
import {
	AcquisitionContext,
	VideoContext,
	SearchContext,
	ArticleContext,
	TrackingContext
} from '@core/models/tracking';

const ACQUISITION_STORAGE_KEY = 'kwo_acquisition';
const ACQUISITION_VERSION_KEY = 'kwo_acquisition_version';
const FIRST_VIDEO_WATCHED_KEY = 'kwo_first_video_watched';
const ATTRIBUTION_WINDOW_DAYS = 30;
const CURRENT_VERSION = '2'; // Increment this to force clear for all users

@Injectable({
	providedIn: 'root'
})
export class TrackingService {

	private configService = inject(ConfigService);
	private videoService = inject(VideoService);
	private hasTrackedFirstVideo = false;

	private acquisitionContext: AcquisitionContext = {};
	private videoContext: VideoContext = {};
	private searchContext: SearchContext = {};
	private articleContext: ArticleContext = {};

	public init(): void {
		mixpanel.init(this.configService.mixpanelToken, {
			autocapture: false,
			autotrack: false,
			ignore_dnt: true,
			record_sessions_percent: 100,
			api_host: 'https://api-eu.mixpanel.com',
		});

		mixpanel.register({
			platform: 'web',
			environment: this.configService.environment,
		});

		this.initializeAcquisitionContext();
	}


	public track(eventName: string, data: Record<string, any> = {}): void {
		const eventData = this.buildEventData(data);
		mixpanel.track(eventName, eventData);
	}

	public identify(id: string, user: CurrentUser): void {
		mixpanel.identify(id);
		mixpanel.people.set({
			$first_name: user.firstName,
			$last_name: user.lastName,
			$email: user.email,
			$subscription: user.subscription,
		});
	}

	public setVideo(context: VideoContext): void {
		this.videoContext = { ...context };
	}

	public setSearch(context: SearchContext): void {
		this.searchContext = { ...context };
	}

	public setArticle(context: ArticleContext): void {
		this.articleContext = { ...context };
	}

	public setAcquisition(context: AcquisitionContext): void {
		// Merge with existing context (don't overwrite URL params)
		this.acquisitionContext = { ...this.acquisitionContext, ...context };
		this.saveToStorage();
	}

	public clearVideo(): void {
		this.videoContext = {};
	}

	public clearSearch(): void {
		this.searchContext = {};
	}

	public clearArticle(): void {
		this.articleContext = {};
	}

	public isFirstVideoWatch(): boolean {
		const hasWatchHistory = this.videoService.watchedList().length > 0;
		if (hasWatchHistory) {
			return false;
		}
		
		if (this.hasTrackedFirstVideo) {
			return false;
		}

		if (localStorage.getItem(FIRST_VIDEO_WATCHED_KEY)) {
			this.hasTrackedFirstVideo = true;
			return false;
		}

		localStorage.setItem(FIRST_VIDEO_WATCHED_KEY, new Date().toISOString());
		this.hasTrackedFirstVideo = true;
		return true;
	}

	public clearAll(): void {
		this.videoContext = {};
		this.searchContext = {};
		this.articleContext = {};
	}


	public getContext(): TrackingContext {
		return this.buildContext();
	}

	private checkAndMigrateVersion(): void {
		const storedVersion = localStorage.getItem(ACQUISITION_VERSION_KEY);
		
		if (storedVersion !== CURRENT_VERSION) {
			// Version mismatch - clear all old tracking data
			localStorage.removeItem(ACQUISITION_STORAGE_KEY);
			localStorage.removeItem(FIRST_VIDEO_WATCHED_KEY);
			localStorage.removeItem(ACQUISITION_VERSION_KEY);
			
			// Set new version
			localStorage.setItem(ACQUISITION_VERSION_KEY, CURRENT_VERSION);
		}
	}

	private initializeAcquisitionContext(): void {
		// Check if version matches, clear old data if not
		this.checkAndMigrateVersion();
		
		// Load stored acquisition (returning visitor)
		this.acquisitionContext = this.loadFromStorage() || {};

		// Read URL params directly from browser (ActivatedRoute doesn't work in root service)
		const params = new URLSearchParams(window.location.search);
		
	const urlContext: AcquisitionContext = {
		utm_tag: params.get('utm_tag') || undefined,
		utm_campaign: params.get('utm_campaign') || undefined,
		acquisition_channel: params.get('acquisition_channel') || undefined,
		acquisition_source: params.get('acquisition_source') || undefined,
		internal_acquisition_source: params.get('internal_acquisition_source') || undefined,
		is_winactie: params.get('is_winactie') === 'true' ? true : undefined,
		winactie_naam: params.get('winactie_naam') || undefined,
		acquisition_content_name: params.get('acquisition_content_name') || undefined,
	};

		const hasUrlParams = Object.values(urlContext).some(v => v !== undefined);
		if (hasUrlParams) {
			this.setAcquisition(urlContext);
		}
	}

	private loadFromStorage(): AcquisitionContext | null {
		const stored = localStorage.getItem(ACQUISITION_STORAGE_KEY);
		
		if (!stored) return null;
		
		try {
			const data = JSON.parse(stored);
			
			// Check if attribution window has expired (30 days)
			if (data.timestamp) {
				const storedDate = new Date(data.timestamp);
				const now = new Date();
				const daysDiff = (now.getTime() - storedDate.getTime()) / (1000 * 60 * 60 * 24);
				
				if (daysDiff > ATTRIBUTION_WINDOW_DAYS) {
					// Attribution window expired, clear storage
					localStorage.removeItem(ACQUISITION_STORAGE_KEY);
					return null;
				}
			}
			
			const context = data.context || data;
			
			// Migrate: Remove old acquisition_source if it exists (now using internal_acquisition_source)
			if (context.acquisition_source && !context.internal_acquisition_source) {
				// Don't migrate - just remove old internal sources
				// acquisition_source should only be for external sources (ads, email, etc.)
				delete context.acquisition_source;
			}
			
			return context;
		} catch (e) {
			// Invalid data, clear it
			localStorage.removeItem(ACQUISITION_STORAGE_KEY);
			return null;
		}
	}

	private saveToStorage(): void {
		const data = {
			context: this.acquisitionContext,
			timestamp: new Date().toISOString()
		};
		localStorage.setItem(ACQUISITION_STORAGE_KEY, JSON.stringify(data));
		// Ensure version is set
		localStorage.setItem(ACQUISITION_VERSION_KEY, CURRENT_VERSION);
	}

	/**
	 * Clear acquisition context.
	 * Call this after successful conversion (signup/purchase).
	 */
	public clearAcquisition(): void {
		this.acquisitionContext = {};
		localStorage.removeItem(ACQUISITION_STORAGE_KEY);
		// Version stays intact - don't remove it
	}

	/**
	 * Track conversion and clear acquisition context.
	 * Use this when user successfully signs up or purchases.
	 */
	public trackConversion(eventName: string, data: Record<string, any> = {}): void {
		// Track the conversion with current acquisition context
		this.track(eventName, data);
		
		// Clear acquisition context after conversion
		// Next acquisition will be tracked fresh
		this.clearAcquisition();
	}

	private buildContext(): TrackingContext {
		return {
			...this.acquisitionContext,
			...this.videoContext,
			...this.searchContext,
			...this.articleContext,
		};
	}

	private buildEventData(additionalData: Record<string, any>): Record<string, any> {
		const context = this.buildContext();
		const merged = { ...context, ...additionalData };

		return this.cleanObject(merged);
	}

	private cleanObject(obj: Record<string, any>): Record<string, any> {
		return Object.fromEntries(
			Object.entries(obj).filter(([_, value]) =>
				value !== undefined &&
				value !== null &&
				value !== ''
			)
		);
	}
	
	public logout(): void {
		mixpanel.reset();
	}
}
